/**
 * Speed HUD — top-right DOM overlay showing game-day counter + 4 speed
 * buttons (Pause / 1× / 2× / 4×) with active-state highlight (v0M3.7b).
 *
 * Consumer creates once via `mountSpeedHud`, gets back a handle to call
 * `update` from the render loop each frame (cheap — only touches DOM
 * when the highlighted button or day counter actually changes).
 */

import { injectMenuStyles } from './menu-styles';
import type { SimSpeed } from '../sim-driver';

export interface SpeedHudCallbacks {
  onSetSpeed: (next: SimSpeed) => void;
  /**
   * v0M15.5-fix (2026-07-29) — clicking the "Menu" button now opens the
   * in-game menu (Resume / Save / Load / Settings / Return to Main /
   * Exit) rather than short-circuiting to a confirm-and-reload.
   * Optional for back-compat with any test/preview mount that doesn't
   * wire it; unwired button falls back to the old confirm+reload
   * behaviour so the HUD keeps working in isolation.
   */
  onOpenMenu?: () => void;
}

export interface SpeedHudHandle {
  /**
   * Sync the HUD to the current speed + day. Should be called every
   * frame; no-op when nothing changed.
   */
  update(speed: SimSpeed, day: number): void;
  /** Remove the HUD from the DOM. */
  destroy(): void;
}

const SPEED_BUTTONS: readonly { label: string; speed: SimSpeed }[] = [
  { label: 'II', speed: 0 },
  { label: '1×', speed: 1 },
  { label: '2×', speed: 2 },
  { label: '4×', speed: 4 },
];

export function mountSpeedHud(
  root: HTMLElement,
  callbacks: SpeedHudCallbacks,
): SpeedHudHandle {
  injectMenuStyles();

  const container = document.createElement('div');
  container.className = 'su-speed-hud';

  const dayEl = document.createElement('div');
  dayEl.className = 'su-speed-hud-day';
  dayEl.textContent = 'Day 0';
  container.appendChild(dayEl);

  const buttonsWrap = document.createElement('div');
  buttonsWrap.className = 'su-speed-hud-buttons';
  container.appendChild(buttonsWrap);

  const btnBySpeed = new Map<SimSpeed, HTMLButtonElement>();
  for (const b of SPEED_BUTTONS) {
    const btn = document.createElement('button');
    btn.className = 'su-speed-hud-btn';
    btn.textContent = b.label;
    btn.title = b.speed === 0 ? 'Pause (Space)' : `${b.speed}× speed`;
    btn.addEventListener('click', () => callbacks.onSetSpeed(b.speed));
    buttonsWrap.appendChild(btn);
    btnBySpeed.set(b.speed, btn);
  }

  // v0M15.5-fix (2026-07-29) — Menu button opens the in-game menu (which
  // exposes Save / Load / Settings / Return to Main / Exit). Was: hard
  // confirm+reload (v0M3.5+fix); the change unifies the two "how do I
  // get to the menu" entry points (Escape key + Menu button) so both
  // route through v0M15.5's paused overlay.
  const menuBtn = document.createElement('button');
  menuBtn.className = 'su-speed-hud-btn';
  menuBtn.textContent = 'Menu';
  menuBtn.title = 'Open menu (Save / Load / Settings / Return to Main)';
  menuBtn.style.marginLeft = '10px';
  menuBtn.addEventListener('click', () => {
    if (callbacks.onOpenMenu) {
      callbacks.onOpenMenu();
      return;
    }
    // Fallback for callers that haven't wired onOpenMenu — preserves
    // the pre-v0M15.5 confirm+reload behaviour so the HUD keeps
    // working in isolated preview/test mounts.
    // eslint-disable-next-line no-alert
    if (window.confirm('Return to main menu? Current game will be lost.')) {
      window.location.reload();
    }
  });
  container.appendChild(menuBtn);

  root.appendChild(container);

  let lastSpeed: SimSpeed | null = null;
  let lastDay: number | null = null;

  function update(speed: SimSpeed, day: number): void {
    if (speed !== lastSpeed) {
      for (const [s, btn] of btnBySpeed) {
        if (s === speed) btn.classList.add('su-active');
        else btn.classList.remove('su-active');
      }
      lastSpeed = speed;
    }
    if (day !== lastDay) {
      dayEl.textContent = `Day ${day}`;
      lastDay = day;
    }
  }

  function destroy(): void {
    container.remove();
  }

  return { update, destroy };
}
